import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findIndex, filter, sortBy } from "lodash/fp";

import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import { UserCalendar } from "@app/analytics/types";
import { get, put } from "@app/common/api";
import useAuth from "@app/common/auth/useAuth";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { replaceAt } from "@app/utils/arrays";

export const getCalendars = async (): Promise<UserCalendar[]> =>
  await get("/calendars", { withCreds: true });

const toggleCalendar = async ({
  id,
  isEnabled,
}: Pick<UserCalendar, "id" | "isEnabled">): Promise<boolean> => {
  const updateBody = { isEnabled };

  return await put(`/calendars/${id}`, updateBody, {
    withCreds: true,
  });
};

const EMPTY_CALENDARS: UserCalendar[] = [];

const useCalendars = (
  { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { hasConnectedCalendarProviders } = useConnectedProviders({ enabled });
  const calendarQueryKey = ["userCalendars", user?.id];

  const calendarsQuery = useQuery({
    queryKey: calendarQueryKey,
    queryFn: getCalendars,
    enabled: hasConnectedCalendarProviders && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const toggleCalendarMutation = useMutation({
    mutationFn: toggleCalendar,
    onMutate: (calendar) => {
      const cachedCalendars: UserCalendar[] =
        queryClient.getQueryData(calendarQueryKey) ?? [];

      const cachedCalendarIndex = findIndex<UserCalendar>(
        { id: calendar.id },
        cachedCalendars
      );

      queryClient.setQueryData(
        calendarQueryKey,
        replaceAt(cachedCalendars, calendar, cachedCalendarIndex)
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: calendarQueryKey });
    },
  });

  const calendars = calendarsQuery.data ?? EMPTY_CALENDARS;

  const calendarsForProvider = (
    provider: CalendarProviderId
  ): UserCalendar[] => {
    return sortBy(
      "id",
      filter((c) => c.provider === provider, calendars)
    );
  };

  return {
    calendars,
    calendarsForProvider,
    ...calendarsQuery,
    toggleCalendar: toggleCalendarMutation,
  };
};

export default useCalendars;
